import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { DeviceOrientationEnum, PlatformService, ScreenService } from '@core/services';
import { BillingV2Service } from '@core/services/lingo2-account/billing-v2.service';
import {
  BillingApiAccountResponseDto,
  BillingApiChargePayCheckProviderResponseDto,
  BillingApiChargePayCheckResponseDto,
  BillingApiChargeResponseDto,
  BillingApiMerchantResponseDto,
} from '@lingo2-billing-sdk/models';
import { BillingCoreChargeStatusEnum } from '@lingo2-billing-sdk/models/src/billing-core/billing-core-charge/types/enums/billing-core-charge-status.enum';
import { BillingCorePaymentStatusEnum } from '@lingo2-billing-sdk/models/src/billing-core/billing-core-payment/types/enums/billing-core-payment-status.enum';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadMe } from '@store/actions/profile.actions';
import { getMe } from '@store/reducers/profile.reducer';
import { CCurrencyAmount, CurrencyEnum, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { zip } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { PaymentConfiguration, PaymentFail, PaymentOptions } from './models';
import {
  IBillingCharge,
  IBillingPaymentMethod,
  PaymentWidgetBillingService,
} from './payment-widget-billing/payment-widget-billing.service';
import { PaymentWidgetMerchantProcessingService } from './payment-widget-merchant-processing/payment-widget-merchant-processing.service';
import { PaymentWidgetService } from './payment-widget.service';

type PaymentWidgetScreenType = 'init' | 'cards' | 'processing' | 'success' | 'fail';

@Component({
  selector: 'app-payment-widget',
  templateUrl: './payment-widget.component.html',
  styleUrls: ['./payment-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentWidgetComponent extends ChangableComponent implements OnInit {
  /** Информация о том, что надо оплатить */
  @Input()
  public options: PaymentOptions;

  /** Информация о том, что нажали на кнопку оплатить */
  @Input()
  public payClicked = false;

  /** Информация о том, что надо обновить список счетов для оплаты */
  @Input() set needUpdate(value: number) {
    if (!value) {
      return;
    }
    this.showAddMethods = false;
    this.configuration = new PaymentConfiguration();
    this.prepare();
  }

  /** Текущее состояние формы и параметры, которые надо сохранить вне биллинга */
  @Output()
  public configured = new EventEmitter<PaymentConfiguration>();

  /** Вызывается когда в виджете 100% определено, что транзакция совершена */
  @Output()
  public succeed = new EventEmitter<boolean>();

  /** Вызывается когда пользователь отменил операцию по своему желанию */
  @Output()
  public canceled = new EventEmitter<boolean>();

  /** Вызывается когда пользователь операция была начата и закончилась ошибкой */
  @Output()
  public failed = new EventEmitter<PaymentFail>();

  /** Вызывается когда в виджете начинается фоновое ожидание выполнения оплаты */
  @Output()
  public processing = new EventEmitter<boolean>();

  /** Вызвать окно добавления нового счета для оплаты (карты) */
  @Output()
  public addAccount = new EventEmitter<string>();

  public screen: PaymentWidgetScreenType = 'init';
  public cardSelected = false;
  public _isAccountPayment = false;
  public configuration = new PaymentConfiguration();
  public paymentMethods: BillingApiAccountResponseDto[] = [];
  public paymentProviders: BillingApiMerchantResponseDto[] = [];
  public payment: BillingApiChargeResponseDto;
  public currencyEnum = CurrencyEnum;
  public errorDescription = '';
  public checkData: BillingApiChargePayCheckResponseDto;
  public selected: IBillingPaymentMethod = null;
  public showAddMethods = false;
  public merchant: BillingApiMerchantResponseDto;

  private trueCharges: BillingApiChargeResponseDto[] = [];
  private charges: IBillingCharge[] = [];
  private approvedProviders: BillingApiChargePayCheckProviderResponseDto[] = [];
  private me: User;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    protected paymentWidgetService: PaymentWidgetService,
    protected screenService: ScreenService,
    public deviceService: DeviceDetectorService,
    public store: Store,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
    protected readonly billingService: PaymentWidgetBillingService,
    protected readonly billingV2Service: BillingV2Service,
    protected readonly merchantService: PaymentWidgetMerchantProcessingService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit() {
    /** Нажатие "Оплатить" снаружи виджета */
    this.paymentWidgetService.pay$
      .pipe(
        filter((value) => !!value),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: () => this.pay(),
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
          this.errorDescription = error.error ? error.error.message : JSON.stringify(error);
          this.screen = 'fail';
          this.failed.emit({ message: this.errorDescription });
          this.detectChanges();
        },
      });

    this.store
      .select(getMe)
      .pipe(
        filter((value) => !!value),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (me) => {
          this.me = me;
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
          this.store.dispatch(loadMe());
        },
      });

    this.prepare();
  }

  public screenClasses() {
    const isPortrait = this.screenService.deviceOrientation === DeviceOrientationEnum.portrait;
    return {
      tablet: this.deviceService.isTablet(),
      mobile: this.deviceService.isMobile(),
      desktop: (!this.deviceService.isTablet() && !this.deviceService.isMobile()) || this.deviceService.isDesktop(),
      portrait: !this.deviceService.isDesktop() && isPortrait,
      landscape: !this.deviceService.isDesktop() && !isPortrait,
    };
  }

  public get formValid() {
    return this.cardSelected;
  }

  /**
   * Пользователь выбрал способ оплаты.
   * Это либо сохранённый способ оплаты (пока только карты)
   * либо доступный провайдер для оплаты новым способом.
   */
  public onSelectPaymentMethod(paymentMethod: IBillingPaymentMethod) {
    if (
      this.configuration.paymentMethod &&
      paymentMethod?.paymentMethod?.cardAccount.accountId ===
        this.configuration.paymentMethod.paymentMethod?.cardAccount.accountId
    ) {
      return;
    }
    if (!paymentMethod) {
      this.cardSelected = false;
      return;
    }
    this.cardSelected = true;

    this.configuration.paymentMethod = paymentMethod;
    this.configuration.provider_id = 'stripe';
    this.merchant = this.paymentProviders.find((merchant) => {
      if (paymentMethod.paymentMethod) {
        return merchant.id === paymentMethod.paymentMethod.providerId;
      } else {
        return merchant.id === paymentMethod?.provider?.id;
      }
    });
    this._isAccountPayment = !!paymentMethod.paymentMethod;
    this.checkPayment(this.trueCharges, paymentMethod.paymentMethod?.cardAccount?.accountId);
    this.detectChanges();
    this.configuration.complete = this.formValid;
    this.configured.emit(this.configuration);
  }

  public get isAccountPayment() {
    return this._isAccountPayment;
  }

  public onSuccess() {
    this.screen = 'success';
    this.processing.emit(false);
    this.detectChanges();
    this.setTimeout(() => {
      this.succeed.emit(true);
      this.detectChanges();
    }, 5000);
  }

  public onFail(err?: PaymentFail) {
    this.screen = 'fail';
    this.errorDescription = err?.message ? err?.message : JSON.stringify(err);
    this.detectChanges();
    this.failed.emit({ message: err?.message || 'Fail' });
    this.processing.emit(false);
  }

  /**
   * ШАГ 1. Загрузка информации о начислениях,
   * сохранённых способах оплаты и доступных
   * платёжных провайдерах.
   */
  private prepare() {
    zip([this.billingV2Service.getCharges(this.options.charge_id), this.billingV2Service.getMerchants()])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([charges, paymentProviders]) => {
          if (this.options.charge_id.length !== charges.length) {
            this.errorDescription = 'Charge(s) not found';
            this.screen = 'fail';
            throw new Error(`Charge(s) not found`);
          }
          if (paymentProviders.length === 0) {
            this.errorDescription = 'No payment providers available';
            this.screen = 'fail';
            throw new Error(`No payment providers available`);
          }
          this.charges = charges;
          this.trueCharges = charges;
          this.paymentProviders = paymentProviders;
          this.checkPayment(this.trueCharges);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
          this.errorDescription = error.error ? error.error.message : JSON.stringify(error);
          this.screen = 'fail';
          this.failed.emit({ message: this.errorDescription });
          this.detectChanges();
        },
      });
  }

  private checkPayment(charges: BillingApiChargeResponseDto[], accountId?: string) {
    if (!charges.length) {
      return;
    }
    const chargesId: string[] = [];
    charges.forEach((item) => {
      chargesId.push(item.id);
    });

    console.log('### currencyCode', this.me.currency_id);
    let currencyCode =
      Object.keys(CurrencyEnum)[Object.values(CurrencyEnum).indexOf(this.me.currency_id)].toUpperCase();
    /**
     * @TODO: удалить из моделей код рубля RUR, оставить только RUB
     * У нас два кода для рубля RUR и RUB.
     * RUR - уже не используется но все равно есть в моделях с таким же ID как и RUB
     */
    if (currencyCode === 'RUR') {
      currencyCode = 'RUB';
    }
    this.billingV2Service
      .payCheck(chargesId, accountId ? accountId : null, currencyCode)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          if (!res) {
            this.errorDescription = 'Check charges failed: ' + chargesId.toString();
            this.screen = 'fail';
            this.failed.emit({ message: 'Check charges failed: ' + chargesId.toString() });
          } else {
            if (res.status === BillingCoreChargeStatusEnum.PAID) {
              this.screen = 'success';
              this.succeed.emit(true);
            } else if (res.status === BillingCoreChargeStatusEnum.REJECT) {
              this.errorDescription = 'Paid rejected';
              this.screen = 'fail';
              this.failed.emit({ message: 'Paid rejected' });
            } else {
              this.screen = 'cards';
              this.checkData = res;
              this.approvedProviders = res.providers;
              this.paymentMethods = res.accounts;
              const amountToPay = new CCurrencyAmount();
              amountToPay.amount = Number(res.amountToPay);
              amountToPay.currency_id = this.currencyEnum[res.currencyCode.toLowerCase()];
              this.paymentWidgetService.payAmount(amountToPay);

              if (!this.paymentMethods.length) {
                this.showAddPaymentMethods();
              }
              this.detectChanges();
            }
          }
        },
        error: (err) => {
          this.errorDescription = err.error ? err.error.message : JSON.stringify(err.message.error.message);
          this.screen = 'fail';
          this.failed.emit({ message: this.errorDescription });
        },
      });
  }

  /**
   * ШАГ 2. Создание платежа и активация компонента банковского процессинга в котором
   * осуществляется непосредственная оплата платежа по ID.
   *
   * Переход осущевляется при условиях, что у правила приняты, а
   * также выбран способ оплаты или провайдер для оплаты новым способом.
   */
  private pay() {
    if (!this.formValid) {
      return;
    }

    if (!this.configuration.paymentMethod) {
      return;
    }

    this.processing.emit(true);
    this.screen = 'processing';

    // Создание платежа.
    // Платёж будет иметь либо сведения для оплаты, если платёж
    // требует участия пользователя (новый способ, 3DSecure и т.п.),
    // либо вернётся со статусом SUCCESS/FAIL при оплате сохраннённым способом.
    this.billingV2Service
      .payBulk(
        this.charges.map((charge) => charge.id),
        this.configuration.paymentMethod,
        {
          accountId: this.configuration.paymentMethod.paymentMethod.accountId,
          firstName: this.options.first_name,
          lastName: this.options.last_name,
          email: this.options.email,
          phone: this.getFormattedPhone(this.options.phone),
          countryCode: this.options.country,
        },
      )
      .pipe(
        map((payment) => {
          if (payment.status === BillingCoreChargeStatusEnum.REJECT) {
            this.errorDescription = 'Charge rejected';
            this.screen = 'fail';
            throw new Error('Charge rejected');
          }
          return payment;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (payment) => {
          switch (payment.payment.status) {
            case BillingCorePaymentStatusEnum.INTENT: // Ручная оплата у мерчанта
              this.payment = payment;
              this.setTimeout(() => this.merchantService.pay(), 500);
              break;

            case BillingCorePaymentStatusEnum.SUCCEEDED: // Сразу оплачен
              this.onSuccess();
              break;

            case BillingCorePaymentStatusEnum.FAILED:
              this.errorDescription = JSON.stringify(payment);
              this.screen = 'fail';
              this.failed.emit({ message: this.errorDescription });
              break;
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'PAYMENT-PROBLEM');
          this.errorDescription = error.error ? error.error.message : JSON.stringify(error);
          this.screen = 'fail';
          this.failed.emit({ message: this.errorDescription });
        },
      });
  }

  public getFormattedPhone(phone: string) {
    if (!phone) {
      return '';
    }
    const formattedPhone = phone.replace(/\D/g, '');
    if (formattedPhone.indexOf('+') !== -1) {
      return formattedPhone;
    } else {
      return `+${formattedPhone}`;
    }
  }

  public addNewAccount(method: any) {
    this.addAccount.emit(method.id);
  }

  /** Если выбран провайдер для нового способа оплаты или уже имеющийся счет */
  public onSelect(method: any, type: 'account' | 'provider'): void {
    if (type === 'account') {
      this.selected = {
        paymentMethod: method,
        provider: null,
      };
    } else if (type === 'provider') {
      this.selected = {
        paymentMethod: null,
        provider: method,
      };
    }
    this.onSelectPaymentMethod(this.selected);

    this.screen = 'processing';
    this.pay();

    this.markForCheck();
  }

  public isForNotRussia(merchant: BillingApiMerchantResponseDto) {
    if (merchant.unSupportCountries) {
      return (merchant.unSupportCountries as any[]).includes('RU');
    } else {
      if (merchant.supportedCountries) {
        return !(merchant.supportedCountries as any[]).includes('RU');
      } else {
        return true;
      }
    }
  }

  public showAddPaymentMethods() {
    this.showAddMethods = true;
    this.detectChanges();
  }

  public closeAddPaymentMethods() {
    if (!this.paymentMethods.length) {
      return;
    }
    this.showAddMethods = false;
    this.detectChanges();
  }

  public get scrollBottom() {
    if (this.payClicked && !this.selected) {
      const alertRef = document.getElementsByClassName('not-selected-payment-method-wrap');
      if (alertRef[0]) {
        this.setTimeout(() => {
          alertRef[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
    return true;
  }

  public isTestProvider(provider: BillingApiMerchantResponseDto) {
    return provider.note && provider.note.toLowerCase().indexOf('test') !== -1;
  }

  public isApprovedProviders(provider: BillingApiMerchantResponseDto) {
    return this.approvedProviders.find((approvedProvider) => approvedProvider.id === provider.id);
  }

  public trackByFn(index) {
    return index;
  }
}
